import { Injectable } from '@angular/core';

interface EssErrorLogger {
    log(message?: any | (() => any), ...additional: any[]): void;
    warn(message?: any | (() => any), ...additional: any[]): void;
    error(message?: any | (() => any), ...additional: any[]): void;

}




const MAX_CAUSE_TEXT_LENGTH = 300;
const EMPTY_CAUSE_TEXT = '(empty)';

const V8_FRAME = /^\s+at\s/; // Chrome, Edge: "    at fn (https://…/main.js:1:2)"
const WEBKIT_FRAME = /^\S*@.*:\d+$/; // Firefox, Safari: "qt@https://…/main.js:3:1"

/**
 * What went wrong underneath, as one short line: `name: message (code …)` for anything error-like
 * (same layout as `objectToText` in functions/src/shared/common/logging.ts), the string itself,
 * `(empty)` when there is nothing to tell. Never the stack of the cause - the report is grouped
 * by the frames of the EssError and has to stay short.
 */
export function causeToText(cause: unknown): string {
    let text: string;
    if (cause === null || cause === undefined) {
        text = '';
    } else if (typeof cause === 'string') {
        text = cause;
    } else if (typeof cause === 'object') {
        const { name, message, code } = cause as { name?: unknown, message?: unknown, code?: unknown };
        const parts = [name, message].filter((part) => typeof part === 'string' && part !== '');
        if (parts.length > 0) {
            text = parts.join(': ') + (code === undefined || code === null ? '' : ` (code ${code})`);
        } else {
            try {
                const json = JSON.stringify(cause);
                text = json === undefined || json === '{}' || json === '[]' ? '' : json;
            } catch {
                text = '(unserializable object)';
            }
        }
    } else {
        text = String(cause);
    }
    // one line only: a line break would end the first line of the report in the middle of the cause
    text = text.replace(/\s*[\r\n]+\s*/g, ' ').trim();
    return text === '' ? EMPTY_CAUSE_TEXT : text.slice(0, MAX_CAUSE_TEXT_LENGTH);
}

/**
 * The text an EssError reports: always `EssError: <message>` first (plus ` | cause: …` when a
 * cause was handed over), then the frames. Chrome starts `stack` with that header line already,
 * Firefox and Safari start with the first frame - their reports used to arrive without any text.
 * Without a cause the Chrome text is the same as `stack`, so existing error groups stay as they are.
 */
export function essErrorLogText(message: string, stack: string | undefined, causeText?: string): string {
    const firstLine = `EssError: ${message}` + (causeText === undefined ? '' : ` | cause: ${causeText}`);
    const stackLines = (stack ?? '').split('\n');
    const firstFrame = stackLines.findIndex((line) => V8_FRAME.test(line) || WEBKIT_FRAME.test(line));
    if (firstFrame === -1) return firstLine;
    return [firstLine, ...stackLines.slice(firstFrame)].join('\n').trimEnd();
}

/**
 * An error class that automatically logs the error that is thrown
 */
export class EssError extends Error {
    private _logger: EssErrorLogger
    /** What the caller caught, when it handed it over - for callers further up. */
    public cause?: unknown
    /**
     * @param cause rest parameter on purpose: a caught `undefined` is a cause too ("(empty)"),
     * only a call without the argument reports no cause
     */
    constructor(logger: EssErrorLogger, message: string, logError?: boolean, ...cause: [unknown?]) {
        super(message); // Pass the message to the Error constructor
        // Set the prototype explicitly to maintain the instanceof functionality
        Object.setPrototypeOf(this, EssError.prototype);
        // Set the name property to the name of the custom error class
        this.name = 'EssError';
        this._logger = logger
        const hasCause = cause.length > 0
        if (hasCause) this.cause = cause[0]
        // one string: the server appends objects behind the frames, which could break the stack detection
        if (logError) this._logger.error(essErrorLogText(message, this.stack, hasCause ? causeToText(cause[0]) : undefined))
    }
}

@Injectable({ providedIn: 'root' })
class LoggerServiceInterface {
    constructor(
    ) {
    }
    log(message?: any | (() => any), ...additional: any[]): void { };
    warn(message?: any | (() => any), ...additional: any[]): void { };
    error(message?: any | (() => any), ...additional: any[]): void { };

}
/**
 * An error service that to create an Error object that automaticall log the error
 */

@Injectable({ providedIn: 'root' })
export class EssErrorService {
    private _logger: LoggerServiceInterface
    private _logError: boolean
    constructor(
    ) {
    }
    public init(loggerService: LoggerServiceInterface, logError: boolean) {
        this._logger = loggerService;
        this._logError = logError
    }

    /**
     * @param logError undefined = the default set in init()
     * @param cause the caught error; its name, message and code go into the logged first line
     */
    newError(message: string, logError?: boolean, ...cause: [unknown?]) {
        return new EssError(this._logger, message, logError === undefined ? this._logError : logError, ...cause)
    }
}
